import type { AgAxisLabelFormatterParams, AgChartTheme } from 'ag-charts-community';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useSecurity } from '../../hooks/useSecurity';
import { format } from '../../utils';

export function createYAxisLabelFormatter(
  defaultPriceIncrement: string | undefined
): ((params: AgAxisLabelFormatterParams) => string) | undefined {
  if (defaultPriceIncrement == null) {
    return undefined;
  }
  return ({ value }: AgAxisLabelFormatterParams) =>
    typeof value === 'number' ? format(value, { spec: defaultPriceIncrement, truncate: true }) : String(value);
}

export function useChartTheme(symbol: string): AgChartTheme<any, never> {
  const theme = useTheme();
  const security = useSecurity(symbol);
  const defaultPriceIncrement = security?.DefaultPriceIncrement;

  return useMemo(() => {
    const labelFormatter = createYAxisLabelFormatter(defaultPriceIncrement);

    return {
      baseTheme: theme.type === 'dark' ? 'ag-financial-dark' : 'ag-financial',
      params: {
        accentColor: theme.colorTextDefault,
        axisColor: theme.borderColorChartAxis,
        backgroundColor: 'transparent',
        gridLineColor: theme.borderColorChartGridLine,
        chromeBackgroundColor: theme.backgroundContent,
        menuBackgroundColor: theme.backgroundPopover,
        menuBorder: false,
        panelBackgroundColor: theme.backgroundModal,
        popupShadow: theme.boxShadowDropdown,
        foregroundColor: theme.colorTextDefault,
        fontFamily: theme.fontFamily,
        focusShadow: 'none',
        fontSize: theme.fontSizeSm * theme.baseSize,
        crosshairLabelBackgroundColor: theme.backgroundPopover,
        crosshairLabelTextColor: theme.colorTextImportant,
        borderRadius: 0,
        tooltipBackgroundColor: theme.backgroundPopover,
        tooltipTextColor: theme.colorTextImportant,
        buttonBackgroundColor: theme.backgroundPopover,
      },
      palette: {
        up: { fill: theme.backgroundChartUpColor, stroke: theme.backgroundChartUpColor },
        down: { fill: theme.backgroundChartDownColor, stroke: theme.backgroundChartDownColor },
      },
      overrides: {
        common: {
          animation: {
            enabled: true,
            duration: 200,
          },
          axes: {
            number: {
              gridLine: {
                enabled: true,
              },
              tick: {
                enabled: true,
              },
              line: {
                enabled: true,
              },
              label: {
                formatter: labelFormatter,
              },
            },
            'ordinal-time': {
              tick: {
                enabled: true,
              },
              gridLine: {
                enabled: true,
              },
              line: {
                enabled: true,
                width: 1,
                stroke: theme.borderColorChartAxis,
              },
            },
          },
        },
      },
    };
  }, [theme, defaultPriceIncrement]);
}
